import {
  Flex,
  Box,
  chakra,
  Text,
  Modal,
  Image,
  Icon,
  ModalOverlay,
  ModalHeader,
  ModalContent,
  ModalFooter,
  Button,
  ButtonGroup,
  Textarea,
  Grid,
  ModalBody,
  FormControl,
  FormLabel,
  Collapse,
  Spacer,
} from "@chakra-ui/react";
import { useMemo, useRef, useState, useEffect } from "react";
import { useProfile } from "@/contexts/ProfileContext";
import { motion } from "framer-motion";
import { FaCheck, FaPlus, FaTrash } from "react-icons/fa";
import {
  COLLEGE_OPTIONS_TOP,
  COLLEGE_RESULTS_COLORS,
  COLLEGE_RESULTS_SORTING,
  LOCKED_TOO_SHORT,
} from "@/utils/profile";
import * as colleges from "@/utils/data/colleges-data";
import { makePlural } from "@/utils/string";
import { createSelectOptions } from "@/utils/ui/select-helper";
import { Select } from "chakra-react-select";
import { AddIcon } from "@chakra-ui/icons";
import ModalReadText from "./view/ModalReadText";

const essayOptions = createSelectOptions([
  "Main Essay",
  "Supplement",
  "University of California Essay",
]);

export const TextDataList = ({ data, isLocked, noWordCount, onClick, sizeLg, sizeSm, ...rest }) => {
  return data.map((obj, i) => {
    const { prompt, word_count } = obj;
    let { content } = obj;
    if (content === LOCKED_TOO_SHORT) {
      // Hide the content altogether - more visually pleasing
      content = "";
    }
    return (
      <Flex
        key={i}
        py={{ base: 2, md: 3 }}
        px={2}
        gap={2}
        border="1px solid gray"
        bg="whiteAlpha.800"
        backdropFilter="blur(3px)"
        cursor="pointer"
        onClick={() => onClick(obj)}
        alignSelf="stretch"
        {...rest}
      >
        {!noWordCount && (
          <Box fontWeight="bold" textAlign="center" w={{ base: 16, md: 28 }}>
            <chakra.span fontSize={sizeLg}>{word_count ?? "?"}</chakra.span>
            <br />
            <chakra.span fontSize={sizeSm}>{makePlural("word", "s", word_count ?? 0)}</chakra.span>
          </Box>
        )}
        <Box flex={1}>
          <Text fontWeight="bold">{prompt}</Text>
          <Text noOfLines={2} mt={noWordCount ? 2 : 0}>
            {content}
          </Text>
          <Text fontStyle="italic" fontSize="sm">
            {isLocked ? "Unlock profile to read full content" : "Click to read full content"}
          </Text>
        </Box>
      </Flex>
    );
  });
};

export const ViewableTextDataList = ({ isLocked, onClickLocked, ...rest }) => {
  const [selected, setSelected] = useState(null);

  return (
    <>
      <TextDataList onClick={isLocked ? onClickLocked : setSelected} {...rest} />
      <ModalReadText isOpen={!!selected} onClose={() => setSelected()} {...(selected ?? {})} />
    </>
  );
};

const EssayGroup = ({
  essays,
  noWordCount,
  type,
  group,
  sizeLg = "3xl",
  sizeSm = "xl",
  collegeResult,
  onClick,
  onClickLocked,
}) => {
  const { isLocked } = useProfile();
  const [expanded, setExpanded] = useState(false);
  const colorScheme =
    type === "college" ? { bg: "white", color: "#000" } : { bg: "black", color: "#fff" };
  const imgSrc = type === "college" && colleges.find(group)?.image;

  if (type === "college") {
    group = colleges.find(group)?.name ?? group;
  }

  if (group === "Main Essay") {
    group = "Personal Statement";
  }

  const onClickEssay = (essay) => {
    if (isLocked) {
      onClickLocked();
    } else {
      onClick(essay);
    }
  };

  return (
    <>
      <Flex
        {...colorScheme}
        alignItems="center"
        alignSelf="stretch"
        pl={2}
        pr={5}
        gap={2}
        border="1px solid black"
        cursor="pointer"
        onClick={() => setExpanded((i) => !i)}
      >
        <Box py={1} fontWeight="bold" textAlign="center" w={{ base: 16, md: 28 }}>
          <chakra.span fontSize={sizeLg}>{essays.length}</chakra.span>
          <br />
          <chakra.span fontSize={sizeSm}>{makePlural("essay", "s", essays.length)}</chakra.span>
        </Box>

        <Box flex={1}>
          <Text fontSize={sizeLg} fontWeight="bold">
            {group.includes("Princeton") ? (
              <Flex
                bg="#E87723"
                justifyContent="center"
                alignItems="center"
                width="fit-content"
                height="36px"
                display="inline-flex"
                transform="auto"
                translateY="0.25em"
                mr={3}
              >
                <Text
                  color="white"
                  fontWeight="bold"
                  fontFamily="sans-serif"
                  fontSize={{ base: "3xl", md: "5xl" }}
                >
                  P
                </Text>
              </Flex>
            ) : (
              imgSrc && (
                <Image
                  src={imgSrc}
                  alt="College logo"
                  display="inline"
                  h="1.2em"
                  transform="auto"
                  translateY="0.25em"
                  pr={3}
                />
              )
            )}

            {group}
          </Text>
        </Box>
        {type === "college" && (
          <Box
            w="10%"
            h="100%"
            bg={COLLEGE_RESULTS_COLORS[collegeResult]}
            transform="auto"
            skewX="-52deg"
            transformOrigin="top left"
          />
        )}
        <motion.span
          animate={{
            rotate: expanded ? "45deg" : "0deg",
            color: expanded ? "#cc0000" : colorScheme.color,
          }}
        >
          <Icon as={FaPlus} boxSize={8} />
        </motion.span>
      </Flex>
      <Collapse in={expanded} style={{ alignSelf: "stretch" }}>
        <TextDataList
          isLocked={isLocked}
          data={essays}
          noWordCount={noWordCount}
          sizeLg={sizeLg}
          sizeSm={sizeSm}
          onClick={onClickEssay}
        />
      </Collapse>
    </>
  );
};

const EssayList = (props) => {
  const { profile } = useProfile();

  const essayGroups = useMemo(
    () =>
      [...new Set(profile.essays.map((essay) => (!essay.college ? essay.type : essay.college)))]
        .map((groupName) => {
          const essays = profile.essays.filter((essay) =>
            !essay.college ? essay.type === groupName : essay.college === groupName
          );
          const groupType = !essays[0].college ? "type" : "college";
          const collegeResult =
            groupType === "college"
              ? profile.colleges.find((college) => college.name === groupName)?.result
              : "";

          return {
            group: groupName,
            type: groupType,
            collegeResult: collegeResult,
            essays: essays,
          };
        })
        .sort((a, b) => {
          if (a.type !== b.type) {
            return a.type === "type" ? -1 : 1;
          } else if (a.type === b.type) {
            if (a.type === "type") {
              return a.group.localeCompare(b.group);
            } /* if (type === 'college) */ else {
              const enrolled = profile.university;
              if (a.group === enrolled || colleges.find(a.group).name === enrolled) return -1;
              if (b.group === enrolled || colleges.find(b.group).name === enrolled) return 1;
              if (a.collegeResult == "") return 1;
              if (b.collegeResult == "") return -1;

              const resultA = COLLEGE_RESULTS_SORTING[a.collegeResult];
              const resultB = COLLEGE_RESULTS_SORTING[b.collegeResult];
              if (resultA !== resultB) return resultA - resultB;
              else return colleges.find(a.group).rank - colleges.find(b.group).rank;
            }
          }
        }),
    [profile]
  );

  return essayGroups.map((group, i) => <EssayGroup {...props} {...group} key={i} />);
};

export const ViewableEssayList = ({ onClickLocked }) => {
  const [readingEssay, setReadingEssay] = useState();

  return (
    <>
      <EssayList
        onClickLocked={onClickLocked}
        onClick={setReadingEssay}
        sizeSm={{ base: "md", md: "xl" }}
        sizeLg={{ base: "lg", md: "3xl" }}
      />
      <ModalReadText
        {...(readingEssay ?? {})}
        isOpen={!!readingEssay}
        onClose={() => setReadingEssay()}
      />
    </>
  );
};

const AddOrEditEssayModal = ({ modalType, essay, onClose, onSave, onDelete }) => {
  const {
    profile: { colleges: profileColleges },
  } = useProfile();

  const [type, setType] = useState(essay.type);
  const [prompt, setPrompt] = useState(essay.prompt);
  const [content, setContent] = useState(essay.content);
  const [college, setCollege] = useState(essay.college);
  const promptPlaceholder =
    type !== "Main Essay"
      ? "Why us? (Max 250 words)"
      : "Some students have a background, identity, interest, or talent that is so meaningful they believe their application would be incomplete without it. If this sounds like you, then please share your story. (Max 650 words)";

  useEffect(() => {
    if (type !== "Supplement") {
      setCollege(null);
    }
  }, [type]);

  const collegeOptions = COLLEGE_OPTIONS_TOP.filter((option) =>
    profileColleges.map((college) => college.name).includes(option.value)
  );

  return (
    <Modal isOpen onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{modalType === "edit" ? "Edit or Delete Essay" : "Add New Essay"}</ModalHeader>
        <ModalBody>
          <Grid templateColumns="repeat(2, 1fr)" gap={6}>
            <FormControl>
              <FormLabel fontWeight="bold">Type of Essay</FormLabel>
              <Select
                value={{ label: type, value: type }}
                options={essayOptions}
                onChange={(e) => setType(e.value)}
              />
            </FormControl>
            {type === "Supplement" && (
              <FormControl>
                <FormLabel>This is a supplemental for&hellip;</FormLabel>
                <Select
                  placeholder="Pick a college from your list..."
                  value={{ label: college, value: college }}
                  options={collegeOptions}
                  onChange={(e) => setCollege(e.value)}
                />
              </FormControl>
            )}
          </Grid>
          <Text mt={4} fontWeight="bold">
            Prompt (include word count):
          </Text>
          <Textarea
            onChange={(e) => setPrompt(e.target.value)}
            value={prompt}
            rows={2}
            placeholder={promptPlaceholder}
          />
          <Text mt={4} fontWeight="bold">
            Essay Content
          </Text>
          <Textarea
            onChange={(e) => setContent(e.target.value)}
            value={content}
            rows={8}
            placeholder="Copy and paste your essay here from Google Docs, Microsoft Word, or any other text editor."
          />
        </ModalBody>
        <ModalFooter>
          <ButtonGroup isAttached>
            {modalType === "edit" && (
              <Button colorScheme="red" onClick={onDelete} leftIcon={<Icon as={FaTrash} />}>
                Delete Essay
              </Button>
            )}
            <Button colorScheme="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => {
                onSave({ type, prompt, content, college });
                onClose();
              }}
              rightIcon={<Icon as={FaCheck} />}
            >
              {modalType === "edit" ? "Save Changes" : "Add Essay"}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const EditableEssayList = ({ onChange }) => {
  const [editingEssay, setEditingEssay] = useState();
  const [showAddEssayModal, setShowAddEssayModal] = useState(false);

  return (
    <>
      <Flex>
        <Spacer />
        <Button
          colorScheme="green"
          leftIcon={<AddIcon />}
          my={4}
          onClick={() => setShowAddEssayModal(true)}
        >
          Add New Essay
        </Button>
      </Flex>
      <EssayList noWordCount onClick={setEditingEssay} sizeLg="2xl" sizeSm="md" />
      {!!editingEssay && (
        <AddOrEditEssayModal
          modalType="edit"
          essay={editingEssay}
          onSave={(newEssay) =>
            onChange((essays) => {
              const newEssays = [...essays];
              newEssays[essays.findIndex((essay) => essay === editingEssay)] = newEssay;
              return newEssays;
            })
          }
          onDelete={() =>
            onChange((essays) => [...essays.filter((essay) => essay !== editingEssay)])
          }
          onClose={() => setEditingEssay(null)}
        />
      )}
      {showAddEssayModal && (
        <AddOrEditEssayModal
          modalType="add"
          essay={{ type: "Main Essay", prompt: "", content: "", college: "" }}
          onSave={(essay) => onChange((essays) => [...essays, essay])}
          onClose={() => setShowAddEssayModal(false)}
        />
      )}
    </>
  );
};

export default EssayList;
